import { useAtomValue } from 'jotai'
import { useRef } from 'react'
import {
	WidgetInputColorField,
	WidgetInputComplexNumericField,
	WidgetInputSliderField,
	WidgetLabel,
	WidgetSelectDropdown,
	type WidgetSelectDropdownOption,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import { externalCanvasDataAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Mask, MaskBlendMode, MaskMode, TextColor, TextColorMode } from '../../data/v1'
import { useResolvedColor } from '../../lib/use-resolved-color'
import { COLOR_REF_FULL_LABELS, COLOR_REF_MODES, ColorDot, type ColorRefMode } from './color-swatches'

const maskModeItems: WidgetSelectGroupItem<MaskMode>[] = [
	{ id: 'none', label: 'None', render: () => <span className="text-xs">None</span> },
	{ id: 'solid', label: 'Solid', render: () => <span className="text-xs">Solid</span> },
	{ id: 'gradient', label: 'Gradient', render: () => <span className="text-xs">Gradient</span> },
]

function BlendModeIcon({ blendMode }: { blendMode: MaskBlendMode }) {
	return (
		<div className="size-4 relative isolate">
			<div
				className="size-3 rounded-full bg-red-500 absolute top-[-1px] left-[-1px]"
				style={{ mixBlendMode: blendMode }}
			/>
			<div
				className="size-3 rounded-full bg-green-500 absolute top-[-1px] right-[-1px]"
				style={{ mixBlendMode: blendMode }}
			/>
			<div
				className="size-3 rounded-full bg-blue-500 absolute bottom-[-1px] left-1/2 -translate-x-1/2"
				style={{ mixBlendMode: blendMode }}
			/>
		</div>
	)
}

const blendModeOptions: WidgetSelectDropdownOption<MaskBlendMode>[] = [
	{
		id: 'normal',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="normal" />Normal
			</div>
		),
	},
	{
		id: 'multiply',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="multiply" />Multiply
			</div>
		),
	},
	{
		id: 'screen',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="screen" />Screen
			</div>
		),
	},
	{
		id: 'overlay',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="overlay" />Overlay
			</div>
		),
	},
	{
		id: 'darken',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="darken" />Darken
			</div>
		),
	},
	{
		id: 'lighten',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="lighten" />Lighten
			</div>
		),
	},
	{
		id: 'color-dodge',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color-dodge" />Color Dodge
			</div>
		),
	},
	{
		id: 'color-burn',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color-burn" />Color Burn
			</div>
		),
	},
	{
		id: 'hard-light',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="hard-light" />Hard Light
			</div>
		),
	},
	{
		id: 'soft-light',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="soft-light" />Soft Light
			</div>
		),
	},
	{
		id: 'difference',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="difference" />Difference
			</div>
		),
	},
	{
		id: 'exclusion',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="exclusion" />Exclusion
			</div>
		),
	},
	{
		id: 'hue',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="hue" />Hue
			</div>
		),
	},
	{
		id: 'saturation',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="saturation" />Saturation
			</div>
		),
	},
	{
		id: 'color',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color" />Color
			</div>
		),
	},
	{
		id: 'luminosity',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="luminosity" />Luminosity
			</div>
		),
	},
]

function MaskColorPicker(
	{ value, onChange, label }: { value: TextColor; onChange: (value: TextColor) => void; label: string },
) {
	const store = useCanvasStore()
	const externalData = useAtomValue(externalCanvasDataAtom, { store })
	const prevModeRef = useRef(value.mode)
	const colorJustSelected = prevModeRef.current !== 'color' && value.mode === 'color'
	prevModeRef.current = value.mode

	const externalColors: Record<ColorRefMode, string> = {
		foregroundColor: externalData.foregroundColor,
		backgroundColor: externalData.backgroundColor,
		accentForegroundColor: externalData.accentForegroundColor,
		accentBackgroundColor: externalData.accentBackgroundColor,
	}

	const options: WidgetSelectDropdownOption<TextColorMode>[] = [
		...COLOR_REF_MODES.map((ref) => ({
			id: ref as TextColorMode,
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={externalColors[ref] || '#ccc'} />
					{COLOR_REF_FULL_LABELS[ref]}
				</div>
			),
		})),
		{
			id: 'color' as TextColorMode,
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={value.color} />
					Custom color
				</div>
			),
		},
	]

	return (
		<>
			<WidgetLabel label={label}>
				<WidgetSelectDropdown
					options={options}
					value={value.mode}
					onChange={(mode) => onChange({ ...value, mode })}
					label={label}
				/>
			</WidgetLabel>

			{value.mode === 'color' && (
				<WidgetInputColorField
					value={value.color}
					onChange={(color) => onChange({ ...value, color })}
					label="Color"
					initialOpen={colorJustSelected}
				/>
			)}
		</>
	)
}

function MaskPreview({ mask }: { mask: Mask }) {
	const solidColor = useResolvedColor(mask.solidColor)
	const gradientStart = useResolvedColor(mask.gradientColorStart)
	const gradientEnd = useResolvedColor(mask.gradientColorEnd)

	if (mask.maskMode === 'none') { return null }

	const style: React.CSSProperties = mask.maskMode === 'solid'
		? { backgroundColor: solidColor, opacity: mask.solidOpacity / 100 }
		: {
			background: gradientStart && gradientEnd
				? `linear-gradient(${mask.gradientAngle}deg, ${toRgba(gradientStart, mask.gradientOpacityStart)}, ${
					toRgba(gradientEnd, mask.gradientOpacityEnd)
				})`
				: undefined,
		}

	return (
		<div className="h-6 rounded border border-grey-200 overflow-hidden">
			<div className="size-full bg-[repeating-conic-gradient(#e5e5e5_0%_25%,#fff_0%_50%)] bg-[size:8px_8px]">
				<div className="size-full" style={{ ...style, mixBlendMode: mask.blendMode }} />
			</div>
		</div>
	)
}

function toRgba(hex: string, opacity: number): string {
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)
	return `rgba(${r},${g},${b},${opacity / 100})`
}

export type MaskInspectorProps = {
	value: Mask
	onChange: (value: Mask) => void
}

export function MaskInspector({ value, onChange }: MaskInspectorProps) {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-sm text-grey-700">Mask Overlay</div>
			<div className="text-xs text-grey-500">
				Cast a color or effect over the background with a masked overlay.
			</div>

			<WidgetLabel label="Mask">
				<WidgetSelectGroup
					value={value.maskMode}
					onChange={(maskMode) => onChange({ ...value, maskMode })}
					items={maskModeItems}
				/>
			</WidgetLabel>

			{value.maskMode !== 'none' && (
				<>
					<MaskPreview mask={value} />

					<WidgetSelectDropdown
						options={blendModeOptions}
						value={value.blendMode}
						onChange={(blendMode) => onChange({ ...value, blendMode })}
						label="Blend mode"
					/>

					{value.maskMode === 'solid' && (
						<>
							<MaskColorPicker
								label="Color"
								value={value.solidColor}
								onChange={(solidColor) => onChange({ ...value, solidColor })}
							/>
							<WidgetLabel label="Opacity">
								<WidgetInputSliderField
									value={value.solidOpacity}
									onChange={(solidOpacity) => onChange({ ...value, solidOpacity })}
									min={0}
									max={100}
									suffix="%"
								/>
							</WidgetLabel>
						</>
					)}

					{value.maskMode === 'gradient' && (
						<>
							<div className="text-[10px] font-medium tracking-wider text-grey-400 uppercase mt-1">
								Start
							</div>
							<MaskColorPicker
								label="Color"
								value={value.gradientColorStart}
								onChange={(gradientColorStart) => onChange({ ...value, gradientColorStart })}
							/>
							<WidgetLabel label="Opacity">
								<WidgetInputSliderField
									value={value.gradientOpacityStart}
									onChange={(gradientOpacityStart) => onChange({ ...value, gradientOpacityStart })}
									min={0}
									max={100}
									suffix="%"
								/>
							</WidgetLabel>

							<div className="text-[10px] font-medium tracking-wider text-grey-400 uppercase mt-1">
								End
							</div>
							<MaskColorPicker
								label="Color"
								value={value.gradientColorEnd}
								onChange={(gradientColorEnd) => onChange({ ...value, gradientColorEnd })}
							/>
							<WidgetLabel label="Opacity">
								<WidgetInputSliderField
									value={value.gradientOpacityEnd}
									onChange={(gradientOpacityEnd) => onChange({ ...value, gradientOpacityEnd })}
									min={0}
									max={100}
									suffix="%"
								/>
							</WidgetLabel>

							<WidgetLabel label="Angle">
								<WidgetInputComplexNumericField
									value={value.gradientAngle}
									onChange={(gradientAngle) => onChange({ ...value, gradientAngle })}
									min={0}
									max={360}
									defaultValue={0}
									suffix="°"
								/>
							</WidgetLabel>
						</>
					)}
				</>
			)}
		</div>
	)
}
